// @nodets/solid — SolidJS primitives that make multicore feel native to Solid's
// reactive model. Heavy work runs on worker threads; the main thread stays smooth.
//
//   import { createParallelResource } from '@nodets/solid'
//   const [results] = createParallelResource(() => items(), heavyFn)
//
// Workers are disposed automatically when the owning component is destroyed.
import {
  createResource,
  onCleanup,
  type Accessor,
  type ResourceReturn,
} from 'solid-js'
import { pool, type Pool, type PoolOptions } from '@nodets/core'

/**
 * Create a worker pool scoped to the current component. The pool is disposed
 * automatically on cleanup. Use it inside event handlers or effects:
 *
 *   const crunch = createParallel((chunk: number[]) => transform(chunk))
 *   const onClick = async () => setOut(await crunch.map(chunks()))
 */
export function createParallel<A, R>(
  task: (arg: A) => R | Promise<R>,
  opts?: PoolOptions
): Pool<A, R> {
  const p = pool(task, opts)
  onCleanup(() => void p.dispose())
  return p
}

/**
 * A Solid resource whose work runs across worker threads. Recomputes whenever
 * `source` changes; `resource.loading` is true while the workers crunch, so the
 * UI never blocks. Workers are disposed on cleanup.
 *
 *   const [sum] = createParallelResource(() => inputs(), heavy)
 *   <Show when={!sum.loading} fallback={<Spinner/>}>{sum()}</Show>
 *
 * A falsy `source` (undefined/null/false) skips execution, matching Solid's
 * createResource convention.
 */
export function createParallelResource<A, R>(
  source: Accessor<readonly A[] | undefined | null | false>,
  task: (arg: A) => R | Promise<R>,
  opts?: PoolOptions
): ResourceReturn<R[]> {
  const p = pool(task, opts)
  onCleanup(() => void p.dispose())
  return createResource(source, (items: readonly A[]) => p.map(items))
}
